import { GenericForm } from '@core/forms/form-group-extensions';
import { valueToEnumKey } from '@shared/utils/helpers';
import { DepthLevelOptions } from '@core/services/depth-level.service';
import { PrimaryLanguage } from '@views/qualification/constants';
import {
  StandardContextEnum,
  StandardProcessEnum,
  StandardStatusEnum,
  StandardVersionStatusEnum
} from '@views/standard/constants';
import { IBaseModel } from '../../../_core/services/models/base/base.model';
import { IQualificationDeveloper } from '../../../_core/services/models/shared/qualification-developer';
import { CosmoBaseModel } from '../../../_core/services/models/base/cosmo-base.model';
import { StandardType } from '@core/services/models/standard-types/standard-types';

export type StandardForm = GenericForm<IStandard>;
export interface IStandard extends IBaseModel {
  /* Allows the properties to be indexed using property names */
  [key: string]: any;
  id: string | null;
  stdType: number | null;
  stdTypeDisplayName: string | null;
  stdNo: string | null;
  externalStdNo: number | null;
  stdNoEntity: number | null;
  verNo: number | null;
  status: StandardStatusEnum | null;
  verStatus: StandardVersionStatusEnum | null;
  ssb: IQualificationDeveloper | null;
  expiring: string | null;
  expiry: string | null;
  reviewDate: string | null;
  process: number | null;
  title: string | undefined | null;
  level: number | null;
  caseId: string | null;
  approvedTeHono: boolean;
  scunq: Classification | null;
  credits: number | null;
  graded: boolean;
  gradingScheme: number | null;
  lastAssessmentDate: string | null;
  primaryLanguage: number | null;
  alternateLanguage: number | null;
  firstPublicationDate: string | null;
  lastPublicationDate: string | null;
  assessmentResourceOnMoeWebsite: boolean;
  maoriContent: boolean;
  purpose: string | null;
  prerequisites: string | null;
  transReady: boolean;
  criteriaForAchievement: string | null;
  criteriaForMerit: string | null;
  criteriaForExcellence: string | null;
  otherAchievementCriteria: StandardAchievementCriteria[];
  guidanceInformation: string | null;
  replacementInformation: string | null;
  subjRef: string | null;
  assessment: number | null;
  assessDatesFirst: string | null;
  gradingType: number | null;
  maoriPaper: boolean;
  maoriAnswer: boolean;
  specialAssistance: boolean;
  ausRef: string | null;
  ausTitle: string | null;
  ausSsb: string | null;
  ausQualFramework: string | null;
  unitCompetencyCode: string | null;
  unitCompetencyName: string | null;
  stdDeveloper: string | null;
  explanatoryNotes: string | null;
  comment: string | null;
  qualityAssurance: string | null;
  created: string | null;
  modified: string | null;
  cmr: Cmr | null;
  outcomes: StandardOutCome[] | null;
  context: StandardContextEnum | null;
  startDate?: string | null;
  endDate?: string | null;
  get standardStatusString(): string;
  get ssbName(): string | undefined;
  get verStatusString(): string;
  associatedCMRs: string[];
  /**
   * (Property of AssociatedStandard, should be moved when differentiating between
   * models and their associated counterparts)
   * Indicates that this standard is associated (to CMR) via classification
   */
  viaClassification: boolean;
  isNew: boolean | null;
  publishToWeb: boolean;
  etag?: string | null;
  /**
   * (Property of AssociatedStandard) Indicates all versions of the standard are linked
   * (the "link record" option) rather than a single version. Displayed as "All".
   */
  isAllVersions?: boolean;

  type?: StandardType;
}

export class Standard extends CosmoBaseModel implements IStandard {
  constructor(obj?: Standard) {
    super();
    Object.assign(this, obj);
  }

  id: string | null = null;
  stdType: number | null = null;
  stdTypeDisplayName: string | null;
  stdNo: string | null = null;
  externalStdNo: number | null = null;
  stdNoEntity: number | null = null;
  verNo: number | null = null;
  status: StandardStatusEnum | null = null;
  verStatus: StandardVersionStatusEnum | null = null;
  ssb: IQualificationDeveloper | null = null;
  expiring: string | null = null;
  expiry: string | null = null;
  reviewDate: string | null = null;
  process: number | null = StandardProcessEnum.New;
  title: string | undefined | null = null;
  level: number | null = null;
  caseId: string | null = null;
  approvedTeHono: boolean = false;
  scunq: Classification | null = null;
  credits: number | null = null;
  graded: boolean = false;
  gradingScheme: number | null = null;
  lastAssessmentDate: string | null = null;
  primaryLanguage: PrimaryLanguage | null = PrimaryLanguage.English;
  alternateLanguage: PrimaryLanguage | null = null;
  firstPublicationDate: string | null = null;
  lastPublicationDate: string | null = null;
  assessmentResourceOnMoeWebsite: boolean = false;
  maoriContent: boolean = false;
  purpose: string | null = null;
  prerequisites: string | null = null;
  transReady: boolean = false;
  criteriaForAchievement: string | null = null;
  criteriaForMerit: string | null = null;
  criteriaForExcellence: string | null = null;
  otherAchievementCriteria: StandardAchievementCriteria[] = [];
  guidanceInformation: string | null = null;
  replacementInformation: string | null = null;
  subjRef: string | null = null;
  assessment: number | null = null;
  assessDatesFirst: string | null = null;
  gradingType: number | null = null;
  maoriPaper: boolean = false;
  maoriAnswer: boolean = false;
  specialAssistance: boolean = false;
  ausRef: string | null = null;
  ausTitle: string | null = null;
  ausSsb: string | null = null;
  ausQualFramework: string | null = null;
  unitCompetencyCode: string | null = null;
  unitCompetencyName: string | null = null;
  stdDeveloper: string | null = null;
  explanatoryNotes: string | null = null;
  comment: string | null = null;
  qualityAssurance: string | null = null;
  created: string | null = null;
  modified: string | null = null;
  publishToWeb: boolean = false;
  cmr: Cmr | null = null;
  outcomes: StandardOutCome[] | null = [];
  context: StandardContextEnum | null = null;
  startDate?: string | null;
  endDate?: string | null;
  /**
   *Getters for JS classes only work when you build a new object using new Standard()
   *It does not work when you get it from the API
   *To solve that one can use the utils.genericItemReMapper or the utils.genericArrayReMapper
   *You can use that in the api call to ensure all objects have all the getters in the class and
   *that the constructor is called
   */

  get standardStatusString(): string {
    return getStandardStatusString(this);
  }

  get ssbName(): string | undefined {
    return this.ssb?.organisationName;
  }

  get verStatusString(): string {
    return getVerStatusString(this) ?? 'ver status error';
  }
  associatedCMRs: string[];
  viaClassification: boolean = false;
  isNew: boolean | null = false;
  isAllVersions?: boolean = false;
}

export function getVerStatusString(standard: IStandard) {
  return valueToEnumKey(StandardVersionStatusEnum, standard.verStatus);
}

export function getStandardStatusString(standard: IStandard): string {
  const standardStatusEnumKey = valueToEnumKey(StandardStatusEnum, standard.status);

  return standardStatusEnumKey!;
}

export class Classification {
  constructor(obj?: Classification) {
    Object.assign(this, obj);
  }

  id: string;
  classificationIdIdentity: number;
  created: string;
  internalId: string;
  externalId: string;
  cfSystemType: number;
  depthLevel: number;
  parent: string;
  caseId: string;
  status: number;
  statusDate: string;
  primaryName: string;
  primaryLanguage: number;
  secondaryName: string;
  secondaryLanguage: number;
  bsas: string;
  sel: string;
  cfdescription: string;
  replacementEffectiveFrom: string;
  replacedBy: string;
  toReplace: string;
  notes: string;
  depthHierarchy: DepthHierarchy;
  classificationDisplay: string;
  startDate?: string | null;
  endDate?: string | null;
  get classificationTypeString(): string {
    return getTypeDisplay(this) ?? '';
  }
  isNew?: boolean | null = false;
}

export function getTypeDisplay(item: Classification) {
  return DepthLevelOptions.find(x => x.value === item.depthLevel && x.systemType === item.cfSystemType)?.description;
}

export interface DepthHierarchy {
  depth1Name: string;
  depth2Name: string;
}

export interface Cmr {
  id: string;
  cmrNo: string;
  cmrNoIdentity: number;
  status: number;
  verStatus: number;
  verNo: number;
  caseId: string;
  title: string;
  ssb: string;
  process: string;
  expiring: string;
  expiry: string;
  reviewDate: string;
  created: string;
  modified: string;
}

export interface StandardAchievementCriteria {
  id: string;
  description: string;
}

export interface StandardOutCome {
  id?: string | null;
  title: string;
  range: string;
  performanceCriteria: StandardPerformanceCriteriaItem[];
}

export interface StandardPerformanceCriteriaItem {
  title: string;
  range: string;
}
